import { getAuth } from "firebase/auth";
import {
  BILLING_FIND_VOUCHER_TYPE,
  isOutwardSupplyRow,
  isInwardSupplyRow,
} from "./billingImportFindService";
import { patchVoucherLinesInWeekLineChunks } from "../features/salesDataImport/services/salesLineChunksService";
import { rowToFirestorePayload } from "../features/salesDataImport/services/validationService";
import { listUploadedWeekPeriods } from "../features/salesDataImport/services/salesLineChunksReader";
import { buildMonthWeekMeta } from "../features/salesDataImport/utils/weekUtils";
import { weekOfMonthFromDate } from "../utils/weeklySalesUpload";

function voucherNoFromRow(row) {
  return String(row?.voucherNo ?? row?.VoucherNo ?? row?.voucherno ?? "").trim();
}

export function productIdFromItem(item) {
  return String(item?.productId ?? item?.productid ?? "").trim();
}

/** Numeric-style voucher no when user saves a brand-new screen (no Find). */
export function generateBillingVoucherNo(date = new Date()) {
  const pad = (n, w = 2) => String(n).padStart(w, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

/** Week bucket from invoice date (same calendar-week slots as Sales Import Center). */
export function resolveBillingSaveWeekMeta(invoiceDateStr) {
  const raw = String(invoiceDateStr || "").trim();
  const d = raw ? new Date(raw) : new Date();
  const date = Number.isNaN(d.getTime()) ? new Date() : d;
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const weekOfMonth = weekOfMonthFromDate(date);
  return buildMonthWeekMeta(year, monthIndex, weekOfMonth);
}

function lineAmount(qty, unitPrice, discount) {
  const q = Number(qty) || 0;
  const r = Number(unitPrice) || 0;
  const d = Number(discount) || 0;
  return Math.round((q * r - d) * 100) / 100;
}

/** Map billing grid lines → sales import row shape (same fields as Excel import). */
function billingItemsToImportRows(items, template, { voucherNo, customerName, tranDate, voucherType, employeeName }) {
  const vn = String(voucherNo).trim();
  const billNo = String(template?.billNo || vn).trim() || vn;
  const billAmount = items.reduce((s, it) => {
    const q = Number(it.qty) || 0;
    if (!q) return s;
    return s + lineAmount(q, it.rate, it.discount);
  }, 0);

  return items
    .filter((it) => String(it.code || "").trim() && Number(it.qty) > 0)
    .map((it) => {
      const qty = Number(it.qty) || 0;
      const unitPrice = Number(it.rate) || 0;
      const discount = Number(it.discount) || 0;
      const amount = lineAmount(qty, unitPrice, discount);
      const pid = productIdFromItem(it) || String(template?.productId || "").trim();
      const desc = String(it.desc || it.alias || template?.itemName || template?.alias || "").trim();
      return {
        valid: true,
        voucherType: voucherType || template?.voucherType || BILLING_FIND_VOUCHER_TYPE,
        voucherNo: vn,
        mVoucherNo: template?.mVoucherNo || "",
        billNo,
        tranDate: tranDate || template?.tranDate || "",
        customerName: customerName || template?.customerName || "",
        employeeName: employeeName || template?.employeeName || "",
        itemGroup: String(it.group ?? it.brand ?? template?.itemGroup ?? "").trim(),
        itemName: desc || String(template?.itemName || "").trim(),
        alias: desc || String(template?.alias || "").trim(),
        partNo: String(it.code || template?.partNo || "").trim(),
        productId: pid,
        qty,
        unitPrice,
        mrp: Number(it.mrp) || Number(template?.mrp) || 0,
        discount,
        discountAmt: discount,
        amount,
        billAmount,
        profitCentre: template?.profitCentre || template?.profitPercent || "",
        profitPercent: template?.profitPercent ?? template?.profitCentre ?? "",
        workstationId: template?.workstationId || "",
        branchId: String(template?.branchId ?? "").trim(),
        periodKey: template?.periodKey || "",
        year: template?.year ?? null,
        month: template?.month ?? null,
        weekOfMonth: template?.weekOfMonth ?? null,
        fromDate: template?.fromDate || "",
        toDate: template?.toDate || "",
      };
    });
}

function weekKey(year, monthIndex, weekOfMonth) {
  return `${year}|${monthIndex}|${weekOfMonth}`;
}

function weeksFromSourceRows(sourceRows, fallback = {}) {
  const map = new Map();
  for (const row of sourceRows || []) {
    const y = Number(row.year ?? fallback.year);
    const m =
      row.month != null
        ? Number(row.month) - 1
        : fallback.monthIndex != null
          ? Number(fallback.monthIndex)
          : null;
    const w = Number(row.weekOfMonth ?? fallback.weekOfMonth);
    if (!y || m == null || !w) continue;
    map.set(weekKey(y, m, w), {
      year: y,
      monthIndex: m,
      weekOfMonth: w,
      periodKey: row.periodKey || fallback.periodKey || "",
      fromDate: row.fromDate || fallback.fromDate || "",
      toDate: row.toDate || fallback.toDate || "",
      fileName: row.fileName || fallback.fileName || "",
    });
  }
  if (!map.size && fallback.year != null && fallback.monthIndex != null && fallback.weekOfMonth) {
    map.set(weekKey(fallback.year, fallback.monthIndex, fallback.weekOfMonth), {
      year: Number(fallback.year),
      monthIndex: Number(fallback.monthIndex),
      weekOfMonth: Number(fallback.weekOfMonth),
      periodKey: fallback.periodKey || "",
      fromDate: fallback.fromDate || "",
      toDate: fallback.toDate || "",
      fileName: fallback.fileName || "",
    });
  }
  return [...map.values()];
}

async function resolveWeekFromPeriodKey(periodKey) {
  const pk = String(periodKey || "").trim();
  if (!pk) return null;
  const weeks = await listUploadedWeekPeriods();
  return weeks.find((w) => w.periodKey === pk) || null;
}

/** Patch voucher lines into sales_import_months lineChunks (import-compatible payloads). */
async function saveVoucherToSalesImportWeek({
  year,
  monthIndex,
  weekOfMonth,
  voucherNo,
  items,
  customerName,
  invoiceDate,
  sourceTemplateRow,
  sourceRows = [],
  audit,
  voucherType = BILLING_FIND_VOUCHER_TYPE,
  isTypeRow = isOutwardSupplyRow,
  employeeName = "",
}) {
  const vn = String(voucherNo).trim();
  const template =
    sourceTemplateRow ||
    (sourceRows || []).find((r) => voucherNoFromRow(r) === vn && isTypeRow(r)) ||
    {};

  const weekTemplate = {
    ...template,
    voucherType: template.voucherType || voucherType,
    periodKey: audit.periodKey || template.periodKey || "",
    year: audit.year ?? year,
    month: audit.monthIndex != null ? audit.monthIndex + 1 : template.month,
    weekOfMonth: audit.weekOfMonth ?? weekOfMonth,
    fromDate: audit.fromDate || template.fromDate || "",
    toDate: audit.toDate || template.toDate || "",
  };

  const newLines = billingItemsToImportRows(items, weekTemplate, {
    voucherNo: vn,
    customerName,
    tranDate: invoiceDate,
    voucherType,
    employeeName,
  });

  if (!newLines.length) {
    throw new Error("No valid item lines to save (need part number, qty > 0, and product id).");
  }

  const auth = getAuth();
  const uploadedBy = auth.currentUser?.email || auth.currentUser?.uid || "billing";
  const uploadedAt = new Date().toISOString();
  const auditMeta = {
    periodKey: audit.periodKey || weekTemplate.periodKey || "",
    fromDate: audit.fromDate || weekTemplate.fromDate || "",
    toDate: audit.toDate || weekTemplate.toDate || "",
    fileName: audit.fileName || weekTemplate.fileName || "billing_save",
    year,
    monthIndex,
    weekOfMonth,
    uploadedAt,
    uploadedBy,
  };

  const newPayloads = newLines.map((r) =>
    rowToFirestorePayload(r, uploadedBy, uploadedAt, auditMeta)
  );

  const stats = await patchVoucherLinesInWeekLineChunks({
    year,
    monthIndex,
    weekOfMonth,
    voucherNo: vn,
    newRowPayloads: newPayloads,
    matchesRow: (r) => voucherNoFromRow(r) === vn && isTypeRow(r),
    audit: auditMeta,
  });

  return { rowCount: newLines.length, weekOfMonth, monthId: `${year}-${String(monthIndex + 1).padStart(2, "0")}`, ...stats };
}

/** Resolve which sales-import week bucket(s) a billing voucher save/delete should target. */
async function resolveBillingTargetWeeks({
  voucherNo,
  invoiceDate,
  sourceRows = [],
  year,
  monthIndex,
  weekOfMonth,
  periodKey,
  isTypeRow = isOutwardSupplyRow,
}) {
  const vn = String(voucherNo || "").trim();
  if (!vn) throw new Error("Voucher number is required");

  const dateWeek = resolveBillingSaveWeekMeta(invoiceDate);

  let weeks = weeksFromSourceRows(sourceRows, {
    year: year ?? dateWeek.year,
    monthIndex: monthIndex ?? dateWeek.monthIndex,
    weekOfMonth: weekOfMonth ?? dateWeek.weekOfMonth,
    periodKey: periodKey || dateWeek.periodKey,
    fromDate: dateWeek.fromDate,
    toDate: dateWeek.toDate,
    fileName: "billing_save",
  });

  if (!weeks.length) {
    const resolved = await resolveWeekFromPeriodKey(periodKey || dateWeek.periodKey);
    if (resolved) {
      weeks = [
        {
          year: resolved.year,
          monthIndex: resolved.monthIndex,
          weekOfMonth: resolved.weekOfMonth,
          periodKey: resolved.periodKey,
          fromDate: resolved.fromDate,
          toDate: resolved.toDate,
          fileName: resolved.fileName || "billing_save",
        },
      ];
    }
  }

  if (!weeks.length) {
    weeks = [
      {
        year: dateWeek.year,
        monthIndex: dateWeek.monthIndex,
        weekOfMonth: dateWeek.weekOfMonth,
        periodKey: dateWeek.periodKey,
        fromDate: dateWeek.fromDate,
        toDate: dateWeek.toDate,
        fileName: "billing_save",
      },
    ];
  }

  const weeksWithVoucher = weeks.filter((w) =>
    (sourceRows || []).some((row) => {
      const y = Number(row.year ?? w.year);
      const m = row.month != null ? Number(row.month) - 1 : w.monthIndex;
      const wk = Number(row.weekOfMonth ?? w.weekOfMonth);
      return (
        voucherNoFromRow(row) === vn &&
        isTypeRow(row) &&
        y === w.year &&
        m === w.monthIndex &&
        wk === w.weekOfMonth
      );
    })
  );
  const targetWeeks = weeksWithVoucher.length ? weeksWithVoucher : weeks.slice(0, 1);

  return { vn, dateWeek, targetWeeks };
}

async function deleteVoucherFromSalesImportWeek({
  year,
  monthIndex,
  weekOfMonth,
  voucherNo,
  isTypeRow = isOutwardSupplyRow,
}) {
  const vn = String(voucherNo || "").trim();
  return patchVoucherLinesInWeekLineChunks({
    year,
    monthIndex,
    weekOfMonth,
    voucherNo: vn,
    newRowPayloads: [],
    matchesRow: (r) => voucherNoFromRow(r) === vn && isTypeRow(r),
  });
}

/**
 * Save billing voucher to sales_import_months / lineChunks (same row shape as import).
 */
export async function saveBillingFindVoucher({
  voucherNo,
  items,
  customerName,
  invoiceDate,
  sourceRows = [],
  sourceTemplateRow = null,
  year,
  monthIndex,
  weekOfMonth,
  periodKey,
  voucherType = BILLING_FIND_VOUCHER_TYPE,
  isTypeRow = isOutwardSupplyRow,
  employeeName = "",
}) {
  const { vn, dateWeek, targetWeeks } = await resolveBillingTargetWeeks({
    voucherNo,
    invoiceDate,
    sourceRows,
    year,
    monthIndex,
    weekOfMonth,
    periodKey,
    isTypeRow,
  });

  const results = [];
  for (const w of targetWeeks) {
    const r = await saveVoucherToSalesImportWeek({
      year: w.year,
      monthIndex: w.monthIndex,
      weekOfMonth: w.weekOfMonth,
      voucherNo: vn,
      items,
      customerName,
      invoiceDate,
      sourceTemplateRow,
      sourceRows,
      audit: w,
      voucherType,
      isTypeRow,
      employeeName,
    });
    results.push(r);
  }

  const monthId = results[0]?.monthId || "";
  return {
    ok: true,
    collection: `sales_import_months/${monthId}/lineChunks`,
    label: `sales_import_months (${results.map((r) => `${r.added || r.rowCount} lines`).join("; ")})`,
    voucherNo: vn,
    weeksSaved: results.length,
    periodKey: targetWeeks[0]?.periodKey || dateWeek.periodKey,
  };
}

/**
 * Remove all lines for this voucher from sales_import_months / lineChunks.
 */
export async function deleteBillingFindVoucher({
  voucherNo,
  invoiceDate,
  sourceRows = [],
  year,
  monthIndex,
  weekOfMonth,
  periodKey,
  voucherType = BILLING_FIND_VOUCHER_TYPE,
  isTypeRow = isOutwardSupplyRow,
}) {
  const { vn, targetWeeks } = await resolveBillingTargetWeeks({
    voucherNo,
    invoiceDate,
    sourceRows,
    year,
    monthIndex,
    weekOfMonth,
    periodKey,
    isTypeRow,
  });

  const tried = new Set();
  let totalRemoved = 0;

  const runDelete = async (w) => {
    const key = weekKey(w.year, w.monthIndex, w.weekOfMonth);
    if (tried.has(key)) return;
    tried.add(key);
    try {
      const stats = await deleteVoucherFromSalesImportWeek({
        year: w.year,
        monthIndex: w.monthIndex,
        weekOfMonth: w.weekOfMonth,
        voucherNo: vn,
        isTypeRow,
      });
      totalRemoved += Number(stats.removed) || 0;
    } catch (err) {
      console.warn("deleteVoucherFromSalesImportWeek:", w.periodKey, err);
    }
  };

  for (const w of targetWeeks) {
    await runDelete(w);
  }

  if (totalRemoved === 0) {
    const allWeeks = await listUploadedWeekPeriods();
    for (const w of allWeeks) {
      await runDelete(w);
    }
  }

  if (totalRemoved === 0) {
    throw new Error(
      `No ${voucherType} lines found for voucher ${vn} in sales_import_months.`
    );
  }

  return {
    ok: true,
    removed: totalRemoved,
    voucherNo: vn,
    collection: "sales_import_months/…/lineChunks",
  };
}
